/**
 * Mission Service
 * 
 * Provides functions for accessing and managing mission data
 * Implements dependency injection for data providers to improve testability
 */

import { Mission, MissionStatus, getMissionStatus } from '../types/mission';
import { 
  getAllMissions as dataGetAllMissions, 
  getMission as dataGetMission,
  getMissionsByStage as dataGetMissionsByStage,
  getAvailableMissions as dataGetAvailableMissions 
} from '../data/missions';

/**
 * Interface for mission data provider
 */
export interface MissionDataProvider {
  getAllMissions(): Mission[];
  getMission(missionId: string): Mission | undefined;
  getMissionsByStage(stage: number): Mission[];
  getAvailableMissions(completedMissions: string[]): Mission[];
}

/**
 * Default implementation that uses the actual data functions
 */
export const defaultMissionDataProvider: MissionDataProvider = {
  getAllMissions: dataGetAllMissions,
  getMission: dataGetMission,
  getMissionsByStage: dataGetMissionsByStage,
  getAvailableMissions: dataGetAvailableMissions
};

/**
 * Get all missions in the game
 * 
 * @param dataProvider Optional data provider for dependency injection
 * @returns Array of all missions
 */
export const getAllMissions = (
  dataProvider: MissionDataProvider = defaultMissionDataProvider
): Mission[] => {
  return dataProvider.getAllMissions();
};

/**
 * Get a specific mission by ID
 * 
 * @param missionId ID of the mission to retrieve
 * @param dataProvider Optional data provider for dependency injection
 * @returns The mission with the specified ID or undefined if not found
 */
export const getMission = (
  missionId: string,
  dataProvider: MissionDataProvider = defaultMissionDataProvider
): Mission | undefined => {
  return dataProvider.getMission(missionId);
};

/**
 * Get all missions for a specific stage
 * 
 * @param stage Stage number
 * @param dataProvider Optional data provider for dependency injection
 * @returns Array of missions in the specified stage
 */
export const getMissionsByStage = (
  stage: number,
  dataProvider: MissionDataProvider = defaultMissionDataProvider
): Mission[] => {
  return dataProvider.getMissionsByStage(stage);
};

/**
 * Get available missions based on user's completed missions
 * 
 * @param completedMissions Array of completed mission IDs
 * @param dataProvider Optional data provider for dependency injection
 * @returns Array of available missions (where all prerequisites are completed)
 */
export const getAvailableMissions = (
  completedMissions: string[],
  dataProvider: MissionDataProvider = defaultMissionDataProvider
): Mission[] => {
  return dataProvider.getAvailableMissions(completedMissions);
};

/**
 * Get all missions with their status based on user's completed missions
 * 
 * @param completedMissions Array of completed mission IDs
 * @param dataProvider Optional data provider for dependency injection
 * @returns Array of missions with their status
 */
export const getMissionsWithStatus = (
  completedMissions: string[],
  dataProvider: MissionDataProvider = defaultMissionDataProvider
): Array<{ mission: Mission, status: MissionStatus }> => {
  return getAllMissions(dataProvider).map(mission => ({
    mission,
    status: getMissionStatus(mission, completedMissions)
  }));
};

export default {
  getAllMissions,
  getMission,
  getMissionsByStage,
  getAvailableMissions,
  getMissionsWithStatus,
  defaultMissionDataProvider
}; 